'use client'

import { useState, useEffect } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Globe, Phone, Mail, MapPin, Calendar, Landmark, Plus, Trash2, ReceiptText, Building2, ExternalLink, Edit } from "lucide-react"
import { Button } from "@/components/ui/button"
import { EditBusinessForm } from "./EditBusinessForm"
import FormModal from "@/components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Edit2, Loader2 } from "lucide-react"
import BusinessService from "@/api/businesses.service";
import { Badge } from '@/components/ui/badge'
import { useToast } from "@/hooks/use-toast"
import { BusinessTaxManagement } from '@/pages/taxes/BusinessTaxInfo';
import { Avatar, AvatarFallback } from "@/components/ui/avatar"

// This type is used to define the shape of our data.
export type Business = {
  id: number;
  publicId: string;
  businessType: string;
  name: string;
  description: string;
  address: string;
  city: string;
  country: string;
  emailAddress: string;
  phoneNumber: string;
  website: string;
  logoImgLink: string;
  bannerImgLink: string;
  dateCreated: string;
}

export const columns: ColumnDef<Business>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="pl-0 font-medium text-sm"
      >
        Business
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => (
      <div className="flex items-center space-x-3">
        {row.original.logoImgLink ? (
          <Avatar className="h-9 w-9">
            <img src={row.original.logoImgLink} alt={row.original.name} className="object-cover" />
          </Avatar>
        ) : (
          <Avatar className="h-9 w-9 bg-primary/10">
            <AvatarFallback className="text-primary font-medium">
              {row.original.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
        )}
        <div>
          <div className="font-medium">{row.original.name}</div>
          <Badge variant="outline" className="mt-1 capitalize text-xs whitespace-nowrap">
            {row.original.businessType.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>
    ),
  },
  {
    accessorKey: "contact",
    header: "Contact Information",
    cell: ({ row }) => {
      const business = row.original;
      return (
        <div className="space-y-1">
          <div className="text-sm flex items-center">
            <Mail className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            <a href={`mailto:${business.emailAddress}`} className="text-primary hover:underline">
              {business.emailAddress}
            </a>
          </div>
          <div className="text-sm flex items-center">
            <Phone className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            <a href={`tel:${business.phoneNumber}`} className="hover:underline">
              {business.phoneNumber}
            </a>
          </div>
          {business.website && (
            <div className="text-sm flex items-center">
              <Globe className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
              <a href={business.website} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">
                {business.website.replace(/^https?:\/\//i, '')}
              </a>
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "location",
    header: "Location",
    cell: ({ row }) => {
      const business = row.original;
      return (
        <div className="text-sm">
          <div className="flex items-start">
            <MapPin className="h-3.5 w-3.5 mr-2 mt-0.5 text-muted-foreground" />
            <div>
              <div>{business.address}</div>
              <div>{business.city}, {business.country}</div>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="text-sm font-medium"
      >
        <Calendar className="w-4 h-4 mr-2" />
        Created
      </Button>
    ),
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.dateCreated}</span>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const { toast } = useToast();

      const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
        toast({
          title: title,
          description: description,
          variant: toastType === "error" ? "destructive" : "default",
          duration: duration,
        });
      };

      const [isModalOpen, setModalOpen] = useState(false);
      const [isBankModalOpen, setBankModalOpen] = useState(false);
      const [isTaxModalOpen, setTaxModalOpen] = useState(false);
      const [bankAccounts, setBankAccounts] = useState([]);
      const [isLoading, setIsLoading] = useState(false);
      const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
      const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
      const [isDeleteLoading, setIsDeleteLoading] = useState(false);

      useEffect(() => {
        if (isBankModalOpen) {
          setIsLoading(true);
          BusinessService.getAllBusinessBankAccounts(row.original.publicId)
            .then(response => {
              if (response.data.result === "success") {
                setBankAccounts(response.data.businessBankAccounts);
              } else {
                showToastWithTimeout("error", "Error fetching bank accounts", response.data.message, 5000);
              }
              setIsLoading(false);
            })
            .catch(error => {
              showToastWithTimeout("error", "Error fetching bank accounts", error.message, 5000);
              setIsLoading(false);
            });
        }
      }, [isBankModalOpen, row.original.id]);

      const handleDeleteAccount = async () => {
        if (selectedAccountId !== null) {
          try {
            setIsDeleteLoading(true);
            const response = await BusinessService.deleteBusinessBankAccount(selectedAccountId);
            console.log("deleteBusinessBankAccount response.data", response.data);
            if (response.data.result === "success") {
              setBankAccounts(bankAccounts.filter((account: any) => account.id !== selectedAccountId));
              showToastWithTimeout("default", "Success", response.data.message, 3000);
            } else {
              showToastWithTimeout("error", "Error", response.data.message || "Failed to delete bank account", 3000);
            }
          } catch (error) {
            showToastWithTimeout("error", "Error", "Failed to delete bank account", 3000);
          } finally {
            setIsDeleteLoading(false);
            setDeleteConfirmOpen(false);
            setSelectedAccountId(null);
          }
        }
      };

      return (
        <>
          <FormModal
            open={isModalOpen}
            setOpen={setModalOpen}
            title="Update Business"
          >
            <EditBusinessForm business={row.original} />
          </FormModal>
          <FormModal
            open={isBankModalOpen}
            setOpen={setBankModalOpen}
            title="Business Bank Accounts"
          >
            <div className="space-y-4">
              {isLoading ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : bankAccounts.length > 0 ? (
                bankAccounts.map((account: any) => (
                  <div key={account.id} className="p-4 border rounded-lg shadow-sm bg-white">
                    <p className="text-sm font-medium text-gray-700">Bank Name: <span className="text-gray-900">{account.bankName}</span></p>
                    <p className="text-sm font-medium text-gray-700">Branch Code: <span className="text-gray-900">{account.branchCode}</span></p>
                    <p className="text-sm font-medium text-gray-700">Account Number: <span className="text-gray-900">{account.accountNumber}</span></p>
                    <p className="text-sm font-medium text-gray-700">Account Type: <span className="text-gray-900">{account.accountType}</span></p>
                    <p className="text-sm font-medium text-gray-700">Currency: <span className="text-gray-900">{account.currency}</span></p>
                    <hr className="my-2" />
                    <Button variant="destructive" onClick={() => { setSelectedAccountId(account.id); setDeleteConfirmOpen(true); }}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No bank accounts found for {row.original.name}</p>
              )}
              <div className="flex justify-end mt-4">
                <Button 
                  variant="default" 
                  onClick={() => window.location.href = `/business/bank-accounts/${row.original.publicId}`}
                >
                  <Landmark className="w-4 h-4 mr-2" />
                  Manage Bank Accounts
                </Button>
              </div>
            </div>
          </FormModal>
          <FormModal
            open={isTaxModalOpen}
            setOpen={setTaxModalOpen}
            title="Business Tax Information"
          >
            <BusinessTaxManagement businessId={row.original.id} />
          </FormModal>
          <FormModal
            open={isDeleteConfirmOpen}
            setOpen={setDeleteConfirmOpen}
            title="Confirm Delete"
          >
            <div className="space-y-4">
              <p>Are you sure you want to delete this bank account?</p>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleDeleteAccount} disabled={isDeleteLoading}>
                  {isDeleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
                </Button>
              </div>
            </div>
          </FormModal>

          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80 hover:bg-primary/10 mr-2"
              onClick={() => window.location.href = `/business/${row.original.publicId}`}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80 hover:bg-primary/10 mr-2"
              onClick={() => window.location.href = `/business/bank-accounts/${row.original.publicId}`}
            >
              <Landmark className="h-4 w-4" />
            </Button>
          
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setModalOpen(true)} className="cursor-pointer flex items-center">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Business
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => window.location.href = `/business/${row.original.publicId}`} className="cursor-pointer flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => window.location.href = `/business/bank-accounts/${row.original.publicId}`} className="cursor-pointer flex items-center">
                  <Landmark className="w-4 h-4 mr-2" />
                  Manage Bank Accounts
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setBankModalOpen(true)} className="cursor-pointer flex items-center">
                  <Landmark className="w-4 h-4 mr-2" />
                  View Bank Accounts
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setTaxModalOpen(true)} className="cursor-pointer flex items-center">
                  <ReceiptText className="w-4 h-4 mr-2" />
                  Manage Tax Info
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      );
    },
  },
]
